<script setup lang="ts">
import { useScrollLock } from '@vueuse/core';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import type { OfferModelShopInfoCoupon } from '~/interfaces/api';

const props = defineProps({
  coupon: {
    type: Object as () => OfferModelShopInfoCoupon,
    required: true,
  },
  copy: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['reset-copy']);

const isCopy = ref(false);
const html = ref<HTMLElement | null>(null);
const isLocked = useScrollLock(html);
const exclusivePromo = computed<any>(() => {
  return props?.coupon?.promocode || '';
});
const discount = computed<any>(() => {
  return props?.coupon?.discount || '';
});
const exclusivePromoName = computed<any>(() => {
  return props?.coupon?.name || '';
});
const exclusiveDescription = computed<any>(() => {
  return props?.coupon?.description || '';
});
const showImg = computed(() => {
  return props?.coupon?.is_exclusive;
});
const mediaStore = useMediaQueryStore();
const isTablet = computed(() => mediaStore.isTablet);
const share = ref<boolean>(false);
const toggleShare = () => {
  isLocked.value = isTablet.value && !share.value;
  share.value = !share.value;
};
const goCopyPromo = () => {
  onCopy(exclusivePromo.value);
};
const onCopy = async (promo: string) => {
  if (window && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(promo);
      // if (!isCopy.value) {
      isCopy.value = true;
      setTimeout(() => {
        emit('reset-copy');
      }, 3000);
      // }
    } catch (e) {}
  }
};
watch(
  () => isTablet.value,
  (value) => {
    if (!value) {
      isLocked.value = false;
    }
  }
);

watch(
  () => props.copy,
  (value) => {
    isCopy.value = value;
    if (props.copy === true) goCopyPromo();
  }
);
onMounted(() => {
  html.value = document.querySelector('html');
});
</script>

<template>
  <div class="exclusive-promo-wrapper">
    <div class="exclusive-promo" @click.prevent="toggleShare">
      <slot />
      <slot name="curshow" />
      <div v-if="exclusivePromoName" class="promo-wrapper">
        <div v-if="showImg" class="exclusive-promo_img">
          <img src="/static/images/crown.svg" alt="crown" />
        </div>
        <p class="exclusive-promo_promo body-m-regular">
          <span v-if="exclusivePromo && discount">
            -{{ discount }} по промокоду {{ exclusivePromo }}
          </span>
        </p>
      </div>
    </div>

    <div
      v-if="exclusivePromo"
      class="exclusive-promo-container-wrapper"
      :class="{ share: share && isTablet }"
    >
      <div class="exclusive-promo-popup-hvostik" />
      <div class="close-div" @click.prevent="toggleShare" />
      <div class="exclusive-promo-container">
        <div class="offer__title">
          Условия акции
          <div class="icon-close" @click.prevent="toggleShare">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.9999 17.9999L6 6"
                stroke="#242424"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M18 6L6 18"
                stroke="#242424"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
        <p class="offer__subtitle">
          {{ exclusiveDescription }}
        </p>
        <p class="offer__text">
          Скопируйте промокод, перейдите в магазин, примените промокод в
          корзине.
        </p>

        <div
          :class="[
            'offer__button',
            'copy__button',
            isCopy ? 'copy__button--on' : 'copy__button--off',
          ]"
          @click="goCopyPromo"
        >
          {{ isCopy ? 'Промокод скопирован' : 'Копировать промокод' }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.exclusive-promo {
  cursor: pointer;

  .promo-wrapper {
    display: flex;
  }

  .exclusive-promo_promo {
    color: $red3;
  }

  .exclusive-promo_img {
    display: flex;
    @include lg-block {
      display: initial;
    }

    img {
      margin-right: 4px;
      width: 18px;
      height: 14px;
      @include lg-block {
        width: 18px;
        height: 14px;
      }
    }
  }
}

.exclusive-promo-wrapper {
  position: relative;
  display: flex;
  align-items: center;

  .social-share-icon {
    display: flex;
  }

  .social-share-image {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }

  .social-share-text {
    font-style: normal;
    font-weight: 450;
    font-size: 14px;
    line-height: 20px;
    color: $main-cta;
    display: none;
    align-items: center;
    @include md-block {
      display: flex;
    }
  }

  .offer__title {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .icon-close {
      position: absolute;
      right: 6px;
      top: -6px;
      width: 24px;
      height: 24px;
      cursor: pointer;
      @include xl-block {
        display: none;
      }
    }

    @include xl-block {
      display: block;
    }
  }

  .exclusive-promo-container-wrapper {
    position: absolute;
    top: -95px;
    right: calc(100% + 12px);
    width: 316px;
    z-index: -2;
    opacity: 0;
    background: $white;
    border: none;
    transition: height 0s 0.2s, background-color 0.2s, opacity 0s;
    border-radius: 0;

    .exclusive-promo-popup-hvostik {
      display: none;
      mask: url('/static/images/social-share-popup-hvostik.svg') center/contain
        no-repeat;
      position: absolute;
      width: 13px;
      height: 8px;
      top: calc(50% - 4px);
      background: white;
      right: -9px;
      rotate: 90deg;
      @include xl-block {
        display: block;
      }
    }

    &.right {
      .exclusive-promo-popup-hvostik {
        right: unset;
        left: 30px;
      }
    }

    &.share {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-color: rgba(0, 0, 0, 0.6);
      width: 100vw;
      height: auto;
      z-index: 153;
      padding-top: 98px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: stretch;
      opacity: 1;
    }

    @include xl-block {
      transition: all 0.2s;
      transition-delay: 0.1s;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(36, 36, 36, 0.2);
    }
  }

  .exclusive-promo-container {
    position: relative;
    display: flex;
    width: 100%;
    background: $white;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 8px 8px 0 0;
    padding: 16px 12px 20px;
    @include lg-block {
      border-radius: 8px;
    }
    @include xl-block {
      top: 4px;
    }
  }

  &:hover {
    .exclusive-promo-container-wrapper {
      @include xl-block {
        opacity: 1;
        z-index: 3;
        transition-delay: 0.1s;
      }
    }
  }

  .close-div {
    display: block;
    height: calc(100% - 204px);
    width: 100%;
    @include xl-block {
      display: none;
    }
  }
}

.offer__title {
  color: $achromatic-black;
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.offer__subtitle {
  color: $achromatic-black;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 8px;
}

.offer__text {
  color: $achromatic-disabled;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 12px;
}

.offer__button {
  border-radius: 4px;
  width: 100%;
  padding: 20px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.copy__button {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  &--off {
    background-color: $main-cta;
    color: $white;
  }

  &--on {
    background-color: $achromatic-card-bg;
    color: $achromatic-black;
  }
}

.smallgrey {
  :deep(path) {
    fill: $achromatic-disabled !important;

    &:active,
    &:hover {
      fill: $achromatic-disabled !important;
    }
  }
}
</style>
